// src/pages/Dashboard.jsx
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import OrderDialog from '../../components/OrderDialog';
import QuoteDialog from '../../components/QuoteDialog';
import SweetTreatDialog from '../../components/SweetTreatDialog';

import { formatDate } from '../../utils/formatDate';
import socket from '../../utils/socket';
import axiosInstance from '../../utils/axiosInstance';
import {
  Inbox,
  CalendarClock,
  MessagesSquare,
  ArrowRight
} from 'lucide-react';
import '../../index.css';

export default function Dashboard() {
  const navigate = useNavigate();

  // Pull EVERYTHING we need from the global layout context
  const {
    // orders
    orders, setOrders, fetchOrders, ordersLoading,
    // quotes (global)
    quotes, quotesLoading,
    // order-status (optional banner)
    isOrderingEnabled,
  } = useOutletContext();

  // Local UI state only
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [unreadIds, setUnreadIds] = useState(new Set());
  const [copyLinkFeedback, setCopyLinkFeedback] = useState(false);
  const notificationSoundRef = useRef(null);
  const copyFeedbackTimeoutRef = useRef(null);

  // Initial loads (orders only; quotes are global)
  useEffect(() => {
    if (orders.length === 0) fetchOrders();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
    };
  }, []);

  // Orders socket
  useEffect(() => {
    notificationSoundRef.current = new Audio('/notification.wav');
    const onNewOrder = (newOrder) => {
      setOrders((prev) => [newOrder, ...prev]);
      setUnreadIds((prev) => new Set(prev).add(newOrder._id));
      notificationSoundRef.current?.play().catch(() => { });
    };
    socket.on('newOrder', onNewOrder);
    return () => socket.off('newOrder', onNewOrder);
    // eslint-disable-next-line
  }, []);

  // Actions
  const handleAccept = async (id) => {
    const { data } = await axiosInstance.put(`/api/orders/${id}/accept`);
    setOrders((prev) => prev.map((o) => (o._id === id ? data : o)));
    setSelectedOrder((prev) => (prev?._id === id ? data : prev));
  };

  const handleReject = async (id) => {
    const { data } = await axiosInstance.put(`/api/orders/${id}/reject`);
    setOrders((prev) => prev.map((o) => (o._id === id ? data : o)));
    setSelectedOrder((prev) => (prev?._id === id ? data : prev));
  };

  const handleUpdateStatus = async (id, status) => {
    await axiosInstance.put(`/api/orders/${id}/update-status`, { status });
    setOrders((prev) => prev.map((o) => (o._id === id ? { ...o, status } : o)));
    setSelectedOrder((prev) => (prev?._id === id ? { ...prev, status } : prev));
  };

  // Derived data
  const newOrders = orders.filter((o) => o.status === 'Pending');

  const isToday = (utcDateString) => {
    const utcDate = new Date(utcDateString);
    const localDate = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000);
    const now = new Date();
    return (
      localDate.getFullYear() === now.getFullYear() &&
      localDate.getMonth() === now.getMonth() &&
      localDate.getDate() === now.getDate()
    );
  };

  const thisWeekOrders = orders.filter((o) => {
    if (o.status !== 'Open') return false;
    const eventDate = new Date(o.eventDate);
    const today = new Date();
    const endOfWeek = new Date();
    today.setHours(0, 0, 0, 0);
    endOfWeek.setHours(0, 0, 0, 0);
    endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
    const fmt = (d) => d.toISOString().split('T')[0];
    return fmt(eventDate) >= fmt(today) && fmt(eventDate) <= fmt(endOfWeek);
  });

  const isUrgent = (date) => (new Date(date) - new Date()) / 864e5 <= 2;

  const handleRowClick = (order) => {
    setSelectedOrder(order);
    setUnreadIds((prev) => {
      const next = new Set(prev);
      next.delete(order._id);
      return next;
    });
  };

  const handleQuoteRowClick = (q) => setSelectedQuote(q);

  const onQuoteChanged = (updated) => {
    // quotes are global; update via setQuotes from context if needed.
    // Here we only update the dialog-local copy (Dashboard displays only "new" quotes).
    setSelectedQuote(updated);
  };

  const onQuoteDeleted = () => setSelectedQuote(null);

  const getOrderTypeMeta = (order) => {
    if (order.orderType === 'sweet') {
      return { label: 'Sweet Treats', classes: 'bg-pink-100 text-pink-700' };
    }
    if (order.cakeShape === 'Cupcakes') {
      return { label: 'Cupcakes', classes: 'bg-amber-100 text-amber-700' };
    }
    if (!order.quoteRequest) {
      return { label: 'Standard Cake', classes: 'bg-blue-100 text-blue-700' };
    }
    return { label: 'Custom Cake', classes: 'bg-purple-100 text-purple-700' };
  };

  const handleCopyCustomerLink = async () => {
    try {
      await navigator.clipboard.writeText(
        "https://candyhavenandcakes.com/special-occasion-cakes#custom-decorations"
      );
      setCopyLinkFeedback(true);
      if (copyFeedbackTimeoutRef.current) clearTimeout(copyFeedbackTimeoutRef.current);
      copyFeedbackTimeoutRef.current = setTimeout(() => setCopyLinkFeedback(false), 2500);
    } catch (err) {
      console.error('Failed to copy customer link:', err);
    }
  };

  // derive “new” quotes from global
  const newQuotes = quotes.filter((q) => q.status === 'new');

  // ─────────────────────────────────────────────────────────────
  // Small UI helpers
  const SectionCard = ({ icon: Icon, title, count, action, children, className = '' }) => (
    <section
      className={`relative flex flex-col min-h-0 h-full rounded-2xl border border-candy-border bg-white/90 shadow-lg ring-1 ring-black/5 ${className}`}
    >
      <div className="flex shrink-0 items-center justify-between px-5 py-4 rounded-t-2xl bg-gradient-to-r from-candy-primary to-candy-primaryDark text-white">
        <div className="flex items-center gap-3 min-w-0">
          <div className="bg-white/15 p-2 rounded-xl shrink-0">
            <Icon size={18} />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold truncate">{title}</h3>
          <span className="ml-2 inline-flex shrink-0 items-center justify-center text-xs font-semibold bg-white/20 px-2 py-0.5 rounded-full">
            {count}
          </span>
        </div>
        {action}
      </div>
      <div className="p-4 sm:p-6 flex-1 min-h-0 overflow-y-auto overscroll-y-contain">
        {children}
      </div>
    </section>
  );

  const rowClasses = (order) =>
    [
      'cursor-pointer transition duration-200 ease-in-out',
      'even:bg-gray-50 hover:bg-candy-background/40',
      unreadIds.has(order._id) && 'outline outline-2 outline-yellow-300/60',
    ]
      .filter(Boolean)
      .join(' ');

  const OrderCard = ({ order }) => (
    <div
      onClick={() => handleRowClick(order)}
      className={`flex items-center justify-between gap-3 p-4 bg-white border border-candy-border rounded-xl shadow-sm hover:shadow-md transition ${unreadIds.has(order._id) ? 'outline outline-2 outline-yellow-300/60' : ''
        }`}
    >
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">
          {order.firstName} {order.lastName}
          {isUrgent(order.eventDate) && <span className="ml-1 text-red-500">🔥</span>}
        </p>
        <p className="text-xs text-gray-500">
          {formatDate(order.eventDate)}
          {isToday(order.eventDate) && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-[10px] font-semibold">
              Today
            </span>
          )}
        </p>
      </div>
      <div className="text-sm text-right whitespace-nowrap">${order.price}</div>
    </div>
  );

  // Mobile-friendly card for quote requests
  const QuoteCard = ({ q }) => (
    <div
      onClick={() => handleQuoteRowClick(q)}
      className="flex items-center justify-between gap-3 p-4 bg-white border border-candy-border rounded-xl shadow-sm hover:shadow-md transition"
    >
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{q.firstName} {q.lastName}</p>
        <p className="text-xs text-gray-500">
          <span className="capitalize">{q.category}</span> • {formatDate(q.date)}
        </p>
      </div>
      <div className="text-sm text-right whitespace-nowrap">{q.servings} ppl</div>
    </div>
  );


  const getPaymentStatus = (order) => {
    // --- DATE CUTOFF LOGIC ---
    const cutoff = new Date("2025-11-19T00:00:00");
    const createdAt = new Date(order.createdAt);

    if (createdAt < cutoff) {
      return (
        <span className="text-gray-500 italic">
          Legacy Order
        </span>
      );
    }

    // --- NORMAL PAYMENT LOGIC ---
    const price = Number(order.price || 0);
    const paid = Number(order.amountPaid || 0);
    const remaining = price - paid;

    if (paid <= 0)
      return <span className="text-red-600 font-semibold">Due ${price.toFixed(2)}</span>;

    if (paid > 0 && paid < price)
      return (
        <span className="text-yellow-600 font-semibold">
          Due – ${remaining.toFixed(2)}
        </span>
      );

    return <span className="text-green-600 font-semibold">Fully Paid</span>;
  };



  return (
    <div className="flex flex-col h-auto lg:h-full min-h-0 px-3 sm:px-4 py-4 sm:py-6 bg-candy-background/60">
      {!isOrderingEnabled && (
        <div className="shrink-0 mb-4">
          <div className="rounded-xl border border-yellow-300 bg-yellow-50 text-yellow-900 px-4 py-3">
            <p className="text-sm">
              ⚠️ Orders are currently <span className="font-semibold">disabled</span>. Enable them in <span className="font-semibold">Settings</span> to accept new requests.
            </p>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2 lg:grid-rows-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-6 lg:h-full lg:overflow-hidden">
      {/* New Orders — left top */}
      <SectionCard
        icon={Inbox}
        title="New Order Requests"
        count={ordersLoading ? '…' : newOrders.length}
        className="lg:col-start-1 lg:row-start-1 min-h-[280px] lg:min-h-0"
      >
        {ordersLoading ? (
          <LoadingOrders />
        ) : newOrders.length === 0 ? (
          <Empty
            emoji="🎉"
            title="No new orders right now!"
            subtitle="You're all caught up. Enjoy the calm! 🧁"
          />
        ) : (
          <>
            <div className="hidden sm:block overflow-x-auto">
              <Table
                head={['Name', 'Pickup Date', 'Type', 'Price', 'Payment']}
                sticky
              >
                {newOrders.map((o) => {
                  const typeMeta = getOrderTypeMeta(o);
                  return (
                  <tr key={o._id} onClick={() => handleRowClick(o)} className={rowClasses(o)}>
                    <Cell>{o.firstName} {o.lastName}</Cell>
                    <Cell>{formatDate(o.eventDate)}</Cell>
                    <Cell>
                      <span
                        className={
                          `px-2 py-1 rounded-full text-xs font-semibold ${typeMeta.classes}`
                        }
                      >
                        {typeMeta.label}
                      </span>
                    </Cell>

                    <Cell>${o.price}</Cell>
                    <Cell>
                      {getPaymentStatus(o)}
                    </Cell>

                  </tr>
                )})}
              </Table>
            </div>
            <div className="sm:hidden space-y-3">
              {newOrders.map((o) => (
                <OrderCard key={o._id} order={o} />
              ))}
            </div>
          </>
        )}
      </SectionCard>

      {/* New Quote Requests — left bottom */}
      <SectionCard
        icon={MessagesSquare}
        title="New Quote Requests"
        count={quotesLoading ? '…' : newQuotes.length}
        className="lg:col-start-1 lg:row-start-2 min-h-[280px] lg:min-h-0"
        action={
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={handleCopyCustomerLink}
              className="inline-flex items-center gap-1 text-xs font-semibold bg-white/15 hover:bg-white/25 transition px-3 py-1.5 rounded-full"
            >
              {copyLinkFeedback ? 'Copied' : 'Copy Link for customer'}
            </button>

            <button
              onClick={() => navigate('/Taylor/quotes')}
              className="inline-flex items-center gap-1 text-xs font-semibold bg-white/15 hover:bg-white/25 transition px-3 py-1.5 rounded-full"
            >
              See all quotes <ArrowRight size={14} />
            </button>
          </div>
        }

      >
        {quotesLoading ? (
          <SkeletonRows rows={4} cols={5} />
        ) : newQuotes.length === 0 ? (
          <Empty
            emoji="💌"
            title="No new quotes right now!"
            subtitle="You’ll see new requests here."
          />
        ) : (
          <>
            <div className="hidden sm:block overflow-x-auto">
              <Table head={['Name', 'Category', 'Event Date', 'Servings']} sticky>
                {newQuotes.map((q) => (
                  <tr
                    key={q._id}
                    className="cursor-pointer transition even:bg-gray-50 hover:bg-candy-background/40"
                    onClick={() => handleQuoteRowClick(q)}
                  >
                    <Cell>{q.firstName} {q.lastName}</Cell>
                    <Cell className="capitalize">{q.category}</Cell>
                    <Cell>{formatDate(q.date)}</Cell>
                    <Cell>{q.servings}</Cell>
                  </tr>
                ))}
              </Table>
            </div>
            <div className="sm:hidden space-y-3">
              {newQuotes.map((q) => (
                <QuoteCard key={q._id} q={q} />
              ))}
            </div>
          </>
        )}
      </SectionCard>

      {/* Orders pending this week — right column, full height */}
      <SectionCard
        icon={CalendarClock}
        title="Orders Pending This Week"
        count={ordersLoading ? '…' : thisWeekOrders.length}
        className="lg:col-start-2 lg:row-start-1 lg:row-span-2 min-h-[320px] lg:min-h-0"
        action={
          <button
            onClick={() => navigate('/Taylor/all-orders')}
            className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold bg-white/15 hover:bg-white/25 transition px-3 py-1.5 rounded-full"
          >
            See all <ArrowRight size={14} />
          </button>
        }
      >
        {ordersLoading ? (
          <LoadingOrders />
        ) : thisWeekOrders.length === 0 ? (
          <Empty
            emoji="📦"
            title="No deliveries scheduled this week!"
            subtitle="Looks like a light week. Take it easy! ☕"
          />
        ) : (
          <>
            <div className="hidden sm:block overflow-x-auto">
              <Table
                head={['Order ID', 'Name', 'Pickup Date', 'Type', 'Price', 'Payment']}
                sticky
              >
                {thisWeekOrders
                  .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate))
                  .map((o) => {
                    const typeMeta = getOrderTypeMeta(o);
                    return (
                    <tr key={o._id} onClick={() => handleRowClick(o)} className={rowClasses(o)}>
                      <Cell>
                        {o.orderId}
                        {isUrgent(o.eventDate) && <span className="ml-1 text-red-500">🔥</span>}
                      </Cell>
                      <Cell className="max-w-[160px] truncate">{o.firstName} {o.lastName}</Cell>
                      <Cell>
                        {formatDate(o.eventDate)}
                        {isToday(o.eventDate) && (
                          <span className="ml-2 px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-[10px] font-semibold">
                            Today
                          </span>
                        )}
                      </Cell>
                      <Cell>
                        <span
                          className={
                            `px-2 py-1 rounded-full text-xs font-semibold ${typeMeta.classes}`
                          }
                        >
                          {typeMeta.label}
                        </span>
                      </Cell>

                      <Cell>${Number(o.price).toFixed(2)}</Cell>
                      <Cell>
                        {getPaymentStatus(o)}
                      </Cell>

                    </tr>
                  )})}
              </Table>
            </div>
            <div className="sm:hidden space-y-3">
              {thisWeekOrders
                .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate))
                .map((o) => (
                  <OrderCard key={o._id} order={o} />
                ))}
            </div>
          </>
        )}
      </SectionCard>
      </div>

      {/* dialogs */}
      {selectedQuote && (
        <QuoteDialog
          quote={selectedQuote}
          onClose={() => setSelectedQuote(null)}
          onChanged={onQuoteChanged}
          onDeleted={onQuoteDeleted}
        />
      )}

      {selectedOrder && (
        selectedOrder.orderType === 'sweet' ? (
          <SweetTreatDialog
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onAccept={handleAccept}
            onReject={handleReject}
            onUpdateStatus={handleUpdateStatus}
          />
        ) : (
          <OrderDialog
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onAccept={handleAccept}
            onReject={handleReject}
            onUpdateStatus={handleUpdateStatus}
          />
        )
      )}
    </div>
  );
}

/* ---------- tiny presentational helpers ---------- */
function Table({ head, children, sticky = false }) {
  return (
    <table className="min-w-full text-sm text-left border-collapse bg-white rounded-xl overflow-hidden">
      <thead className="bg-gray-300 text-gray-700">
        <tr>
          {head.map((h) => (
            <th
              key={h}
              className={`px-6 py-3 font-semibold uppercase tracking-wide ${sticky ? 'sticky top-0 z-10' : ''
                }`}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">{children}</tbody>
    </table>
  );
}

function Cell({ children, className = '' }) {
  return <td className={`px-6 py-3 whitespace-nowrap ${className}`}>{children}</td>;
}

function Empty({ emoji, title, subtitle }) {
  return (
    <div className="flex flex-col items-center text-center py-12">
      <span className="text-4xl mb-3">{emoji}</span>
      <p className="text-base sm:text-lg font-semibold">{title}</p>
      <span className="text-sm text-gray-500 mt-1">{subtitle}</span>
    </div>
  );
}

function SkeletonRows({ rows = 4, cols = 5 }) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200">
      <div className="animate-pulse">
        {[...Array(rows)].map((_, r) => (
          <div key={r} className="grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {[...Array(cols)].map((__, c) => (
              <div key={c} className="h-10 border-b border-gray-100 bg-gray-100/70" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* Matches the spinner style you used in AllOrders.jsx */
function LoadingOrders() {
  return (
    <div className="flex justify-center items-center py-12">
      <svg className="animate-spin h-8 w-8 text-candy-primary" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
        <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" className="opacity-75" />
      </svg>
      <span className="ml-3 text-candy-primary font-semibold text-lg">Loading orders…</span>
    </div>
  );
}
